import { bind, extend } from './utils';
const METHODS_ACCEPT_BODY = ['post', 'put', 'delete', 'patch'];
export function handlePathPlaceholders(apiUrl, pathParams) {
    if (!pathParams) {
        return apiUrl;
    }
    return Object.entries(pathParams).reduce((acc, [pathKey, value]) => acc.replace(`{${pathKey}}`, value), apiUrl);
}
export function handleQueryParams(apiUrl, queryParams) {
    if (!queryParams) {
        return apiUrl;
    }
    const paramString = Object.entries(queryParams)
        .map(([key, val]) => `${key}=${val}`)
        .join('&');
    return `${apiUrl}?${paramString}`;
}
const defaultErrorHandler = (meta) => {
    return new Error(JSON.stringify({
        status: meta.status,
        statusText: meta.statusText,
        url: meta.url,
        traceId: meta === null || meta === void 0 ? void 0 : meta.traceId,
        body: meta === null || meta === void 0 ? void 0 : meta.body
    }));
};
const getFilenameFromContentDispositionHeader = (header) => {
    var _a, _b;
    if (header === null) {
        return '';
    }
    const contentDisposition = header === null || header === void 0 ? void 0 : header.split(';');
    const fileNameToken = `filename*=UTF-8''`;
    const fileName = (_b = (_a = contentDisposition
        .find((value) => value.includes(fileNameToken))) === null || _a === void 0 ? void 0 : _a.trim()) === null || _b === void 0 ? void 0 : _b.replace(fileNameToken, '');
    return fileName !== null && fileName !== void 0 ? fileName : '';
};
export const parseJSONOr = (errMessage, fallbackValue) => {
    try {
        const parsed = JSON.parse(errMessage);
        return parsed;
    }
    catch (error) {
        return fallbackValue;
    }
};
const defaultParseBody = async (response) => {
    const contentType = response.headers.get('content-type');
    const isText = contentType === null || contentType === void 0 ? void 0 : contentType.startsWith('text');
    if (!contentType)
        return null;
    if (isText)
        return response.text();
    return response.json();
};
class HttpClient {
    constructor(errorHandler = defaultErrorHandler, getToken, getDefaultHeaders) {
        this.errorHandler = errorHandler;
        this.getToken = getToken;
        this.getDefaultHeaders = getDefaultHeaders;
        this.interceptors = {
            request: new Set(),
            response: new Set()
        };
    }
    _getHeaders(options) {
        var _a;
        const getter = (_a = options === null || options === void 0 ? void 0 : options.getDefaultHeaders) !== null && _a !== void 0 ? _a : this.getDefaultHeaders;
        return getter ? getter() : {};
    }
    async _getToken(options) {
        var _a;
        const getter = (_a = options === null || options === void 0 ? void 0 : options.getToken) !== null && _a !== void 0 ? _a : this.getToken;
        return getter && (await getter());
    }
    _errorHandler(error, options) {
        var _a;
        const handler = (_a = options === null || options === void 0 ? void 0 : options.errorHandler) !== null && _a !== void 0 ? _a : this.errorHandler;
        return handler(error);
    }
    async request(_apiUrl = '', _opts, clientOptions) {
        var _a, _b, _c, _d;
        let apiUrl = _apiUrl;
        let opts = _opts;
        this.interceptors.request.forEach((interceptor) => {
            [apiUrl, opts] = interceptor(apiUrl, opts);
        });
        const pathReplacedUrl = handlePathPlaceholders(apiUrl, (_a = opts === null || opts === void 0 ? void 0 : opts.params) === null || _a === void 0 ? void 0 : _a.pathParams);
        const urlWithQueryParams = handleQueryParams(pathReplacedUrl, (_b = opts === null || opts === void 0 ? void 0 : opts.params) === null || _b === void 0 ? void 0 : _b.queryParams);
        const token = (opts === null || opts === void 0 ? void 0 : opts.token) ? opts.token : await this._getToken(clientOptions);
        const authHeader = { Authorization: `Bearer ${token || ''}` };
        const defaultHeaders = this._getHeaders(clientOptions);
        const baseFetchConfig = {
            method: (opts === null || opts === void 0 ? void 0 : opts.method) || 'get',
            signal: opts === null || opts === void 0 ? void 0 : opts.signal,
            headers: (opts === null || opts === void 0 ? void 0 : opts.headers)
                ? { ...authHeader, ...defaultHeaders, ...opts === null || opts === void 0 ? void 0 : opts.headers }
                : { ...authHeader, ...defaultHeaders, 'Content-Type': 'application/json' }
        };
        const fetchConfig = METHODS_ACCEPT_BODY.includes(baseFetchConfig.method.toLowerCase()) && ((_c = opts === null || opts === void 0 ? void 0 : opts.params) === null || _c === void 0 ? void 0 : _c.body)
            ? { ...baseFetchConfig, body: opts.params.body }
            : baseFetchConfig;
        fetchConfig.method = fetchConfig.method.toUpperCase();
        const bodyParse = (_d = opts === null || opts === void 0 ? void 0 : opts.bodyParse) !== null && _d !== void 0 ? _d : defaultParseBody;
        return await fetch(urlWithQueryParams, fetchConfig)
            .then(async (response) => {
            let body;
            // TODO: https://wcloud.atlassian.net/browse/ICP-1764
            if (opts === null || opts === void 0 ? void 0 : opts.blobResponse) {
                body = {
                    blob: await response.blob(),
                    name: getFilenameFromContentDispositionHeader(response.headers.get('content-disposition'))
                };
            }
            else {
                body = await bodyParse(response);
            }
            this.interceptors.response.forEach((interceptor) => {
                interceptor({ url: response.url, status: response.status });
            });
            if (response.status >= 400) {
                const errorMetaInfo = {
                    status: response.status,
                    statusText: response.statusText,
                    traceId: response.headers.get('traceId') || (opts === null || opts === void 0 ? void 0 : opts.traceId),
                    body,
                    url: response.url
                };
                throw new Error(JSON.stringify(errorMetaInfo));
            }
            return (opts === null || opts === void 0 ? void 0 : opts.needFullResponse) ? response : body;
        })
            .catch((error) => {
            if (opts === null || opts === void 0 ? void 0 : opts.errorHandler) {
                opts === null || opts === void 0 ? void 0 : opts.errorHandler(error);
            }
            else {
                let errObj;
                try {
                    errObj = JSON.parse(error.message);
                }
                catch (error) {
                    throw new Error('Unexpected value in JSON');
                }
                throw this._errorHandler(errObj, clientOptions);
            }
        });
    }
}
export const baseHttpClient = createHttpClient();
function createHttpClient() {
    const context = new HttpClient();
    const instance = bind(HttpClient.prototype.request, context);
    extend(instance, HttpClient.prototype, context, { allOwnKeys: true });
    extend(instance, context, null, { allOwnKeys: true });
    // @ts-ignore
    return instance;
}
export const getHttpClient = ({ errorHandler = defaultErrorHandler, getToken, getDefaultHeaders }) => {
    return (apiUrl, opts) => {
        return baseHttpClient.request(apiUrl, opts, { errorHandler, getToken, getDefaultHeaders });
    };
};
